import SentimentAngry from '@/images/sentiment/SentimentAngry.svg';
import SentimentEmpathetic from '@/images/sentiment/SentimentEmpathetic.svg';
import SentimentExcited from '@/images/sentiment/SentimentExcited.svg';
import SentimentFriendly from '@/images/sentiment/SentimentFriendly.svg';
import SentimentProfessional from '@/images/sentiment/SentimentProfessional.svg';
import { SectionContainer } from '@/pages/AdvancedEditor/components/panels/common/SectionContainer';
import { StyledText } from '@/pages/AdvancedEditor/components/panels/common/SectionTitle';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import {
    PolotnoStoreActivePageKeys,
    usePolotnoActivePageState,
} from '@/pages/AdvancedEditor/hooks/polotno-store/usePolotnoActivePageState';
import { getVoiceFromCurrentSentiment } from '@/pages/AdvancedEditor/utils/expressive-utils';
import { getAvatarPresenterFromPage } from '@/pages/AdvancedEditor/utils/getAvatarFromPage';
import { usePreloadVideos } from '@/pages/AdvancedEditor/utils/preloadVideos';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import { AvatarSentiment, VideoType } from '@d-id/types';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { SentimentModal } from './SentimentModal';

const SentimentButtonContainer = styled.div<{ isLoading: boolean; disabled?: boolean }>`
    padding: 12px;
    height: 53px;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};
    cursor: ${({ isLoading }) => (isLoading ? 'default' : 'pointer')};

    &:hover {
        ${({ isLoading }) => !isLoading && `border: 1px solid ${theme.colors.secondary.main};`}
    }

    ${({ disabled }) =>
        disabled &&
        `
        cursor: not-allowed;
        border: 1px solid ${theme.colors.black[300]} !important;
        opacity: 0.5;
    `}
`;

const SentimentName = styled.span`
    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 16px;
    font-weight: 500;
    line-height: 28.171px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const SentimentIcon = styled.img`
    width: 24px;
    height: 24px;
`;

const ModalButton = styled(ButtonBase)`
    display: flex;
    padding: 2px 8px;
    color: ${({ theme }) => theme.colors.secondary[900]};
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
    background-color: ${theme.colors.black[100]};
    border-radius: 8px;
    margin-left: auto;
    min-width: 72px;
    min-height: 24px;

    :hover {
        background-color: ${theme.colors.black[200]};
    }
`;

const sentimentImageSources: Record<AvatarSentiment, string> = {
    [AvatarSentiment.Friendly]: SentimentFriendly,
    [AvatarSentiment.Professional]: SentimentProfessional,
    [AvatarSentiment.Empathetic]: SentimentEmpathetic,
    [AvatarSentiment.Excited]: SentimentExcited,
    [AvatarSentiment.Frustrated]: SentimentAngry,
};

export const SentimentControl = observer(() => {
    const { store } = useAdvancedEditorContext();
    const sentimentControlEnabled = useFlag('expressive_avatars', false);

    const avatarPresenter = getAvatarPresenterFromPage(store.activePage);
    const [ttsSelectedVoice, setTtsSelectedVoice] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.ttsSelectedVoice);
    const [selectedSentimentId, setSelectedSentimentId] = usePolotnoActivePageState(PolotnoStoreActivePageKeys.selectedSentimentId);

    const [sentimentModalState, setSentimentModalState] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const avatarSentiments = avatarPresenter?.sentiments || [];
    const selectedSentiment = avatarSentiments.find(sentiment => sentiment.id === selectedSentimentId);

    const videoUrls = avatarSentiments.map(sentiment => sentiment.preview_url);
    const preloadVideos = usePreloadVideos(videoUrls);

    if (!sentimentControlEnabled || avatarPresenter?.type !== VideoType.EXPRESSIVE || avatarSentiments.length <= 1 || !selectedSentiment)
        return null;

    const handleOpenModal = async () => {
        setIsLoading(true);

        await preloadVideos();

        setIsLoading(false);
        setSentimentModalState(true);
    };

    const handleSentimentConfirm = (sentiment: AvatarSentiment) => {
        const sentimentId = avatarSentiments.find(s => s.sentiment === sentiment)?.id;

        if (!sentimentId) return;

        setSelectedSentimentId(sentimentId);

        const currentVoiceSupportsSentiment = avatarSentiments.some(sentiment => sentiment.voice?.voice_id === ttsSelectedVoice?.id);
        if (currentVoiceSupportsSentiment) {
            const voiceForNewSentiment = getVoiceFromCurrentSentiment(sentimentId, avatarPresenter);
            if (voiceForNewSentiment) {
                setTtsSelectedVoice(voiceForNewSentiment);
            }
        }
    };

    return (
        <SectionContainer>
            <StyledText>Sentiment</StyledText>

            <SentimentButtonContainer isLoading={isLoading} onClick={isLoading ? () => {} : handleOpenModal}>
                <SentimentIcon src={sentimentImageSources[selectedSentiment.sentiment]} alt={selectedSentiment.sentiment} />
                <SentimentName>{selectedSentiment.sentiment}</SentimentName>
                <ModalButton disabled={isLoading}>{isLoading ? <CircularProgress size={16} color="inherit" /> : 'Change'}</ModalButton>
            </SentimentButtonContainer>

            <SentimentModal
                open={sentimentModalState}
                onClose={() => setSentimentModalState(false)}
                availableSentiments={avatarSentiments}
                value={selectedSentiment.sentiment}
                onConfirm={handleSentimentConfirm}
            />
        </SectionContainer>
    );
});
